import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe } from '@jsverse/transloco';
import { BirthdateErrorPipe, BirthdatePipe, CustomerIdPipe, DayPipe, MonthPipe, YearPipe } from '@zero/onboarding/domain';
import { InputComponent } from '@zero/shared/ui-web-design-system';
import { BehaviorSubject, combineLatest, filter, firstValueFrom, map } from 'rxjs';
import { BaseComponent } from '../../components/base.component';
import { OnboardingContainerComponent } from '../../components/onboarding-container/onboarding-container.component';

@Component({
  imports: [OnboardingContainerComponent, TranslocoPipe, AsyncPipe, InputComponent, CustomerIdPipe, DayPipe, MonthPipe, YearPipe],
  templateUrl: './legal-guardian-2.component.html',
})
export class LegalGuardian2Component extends BaseComponent {
  readonly #destroyRef = inject(DestroyRef);

  readonly #customerId = new BehaviorSubject<string>(undefined);
  readonly #birthDay = new BehaviorSubject<string>(undefined);
  readonly #birthMonth = new BehaviorSubject<string>(undefined);
  readonly #birthYear = new BehaviorSubject<string>(undefined);
  readonly #focussedField = new BehaviorSubject<'birthDay' | 'birthMonth' | 'birthYear'>(undefined);

  public readonly customerId$ = this.#customerId.asObservable();
  public readonly birthDay$ = this.#birthDay.asObservable();
  public readonly birthMonth$ = this.#birthMonth.asObservable();
  public readonly birthYear$ = this.#birthYear.asObservable();
  public readonly birthdateError$ = combineLatest([this.birthDay$, this.birthMonth$, this.birthYear$]).pipe(
    map(([day, month, year]) => day && month && year && new BirthdateErrorPipe().transform(day, month, year)),
  );
  public readonly isFormValid$ = combineLatest([
    this.customerId$,
    this.birthDay$,
    this.birthMonth$,
    this.birthYear$,
    this.store.sendingLegalGuardian2Information$,
  ]).pipe(
    map(
      ([customerId, birthDay, birthMonth, birthYear, sendingLegalGuardian2Information]) =>
        customerId &&
        !new CustomerIdPipe().transform(customerId) &&
        new BirthdatePipe().transform(birthDay, birthMonth, birthYear) &&
        !sendingLegalGuardian2Information,
    ),
  );

  public readonly focussedField$ = this.#focussedField.asObservable();

  protected override async submit(): Promise<void> {
    (await firstValueFrom(this.isFormValid$)) &&
      this.store.sendLegalGuardian2Information(
        this.#customerId.value,
        `${this.#birthYear.value}-${this.#birthMonth.value}-${this.#birthDay.value}`,
      );
  }

  protected override populateFromData(): void {
    combineLatest([this.store.selectedChildOnboarding$])
      .pipe(
        filter(([data]) => !!data),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(async ([{ legalGuardian2Id, legalGuardian2Birthday }]) => {
        this.#customerId.next(legalGuardian2Id);
        this.#setBirthday(legalGuardian2Birthday);
      });
  }

  protected override listenToSuccessfulSubmit(): void {
    this.store.sentLegalGuardian2InformationSuccessfully$
      .pipe(
        filter((sent) => sent),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.service.goToNextStep());
  }

  public handleCustomerIdChange = (customerId: string) => this.#customerId.next(customerId);
  public handleBirthDayChange = (birthDay: string) => {
    this.#birthDay.next(birthDay);
    new DayPipe().transform(birthDay) && this.#focussedField.next('birthMonth');
  };
  public handleBirthMonthChange = (birthMonth: string) => {
    this.#birthMonth.next(birthMonth);
    new MonthPipe().transform(birthMonth) && this.#focussedField.next('birthYear');
  };
  public handleBirthYearChange = (birthYear: string) => this.#birthYear.next(birthYear);

  #setBirthday(birthday: Date | string) {
    if (!birthday) {
      return;
    }
    const birthDate = new Date(birthday);
    const day = birthDate.getDate();
    this.#birthDay.next(day < 10 ? `0${day.toString()}` : day.toString());
    const month = birthDate.getMonth() + 1;
    this.#birthMonth.next(month < 10 ? `0${month.toString()}` : month.toString());
    this.#birthYear.next(birthDate.getFullYear().toString());
  }
}
